import { useEffect, useState } from 'react';

import { MessageCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { CollapsibleNavGroupHeader } from '@/components/organisms/Navbar/CollapsibleNavGroupHeader/CollapsibleNavGroupHeader';
import SubNavShell from '@/components/organisms/Navbar/SubNavShell/SubNavShell';
import useDesks from '@/hooks/useDesks';
import { cn } from '@/lib/utils';
import { CreateReportPopover } from '@/modules/analytics/components/CustomReports/CreateReportPopover';
import { useReportPermissions } from '@/modules/analytics/hooks/useReportPermissions';
import { selectAccountSlug } from '@/redux/session/selectors';

import { getReportOptions } from './constants';
import { navLinkClass } from './navLinkClass';
import { ReportNavItems } from './ReportNavItems';

const STORAGE_KEY = 'analytics-nav:expanded-groups';

const HUMAN_AGENTS_LINK = '/human-agents';

const readStoredRecord = (key: string): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

const AnalyticsNavigation = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { serviceDesks, listStatus } = useDesks();
  const hideHumanAgents = listStatus === 'success' && !serviceDesks?.length;
  const { canRead: canReadReports, canWrite: canWriteReports } = useReportPermissions();

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    readStoredRecord(STORAGE_KEY)
  );

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedGroups));
    } catch {
      // storage may be unavailable; ignore
    }
  }, [expandedGroups]);

  return (
    <SubNavShell>
      {getReportOptions().map((option, groupIndex) => {
        if (option.type === 'standalone') {
          return (
            <div key={option.link} className={cn(groupIndex > 0 && 'mt-1')}>
              <NavLink
                to={`/${slug}/analytics${option.link}`}
                className={({ isActive }) =>
                  cn(
                    'flex w-full items-center rounded border border-transparent transition-colors hover:bg-highlight-background',
                    isActive && 'border-border bg-background'
                  )
                }
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                  <MessageCircleIcon className="h-4 w-4 text-foreground-muted" />
                </span>
                <span className="flex-1 px-1.5 py-1.5 text-sm font-medium text-foreground">
                  {t(option.text_key)}
                </span>
              </NavLink>
            </div>
          );
        }

        const isExpanded = expandedGroups[option.title_key] ?? true;
        const isReportsGroup = option.title_key === 'analytics.personalized_metrics';

        return (
          <div key={option.title_key} className={cn('mb-1', groupIndex > 0 && 'mt-1')}>
            <CollapsibleNavGroupHeader
              label={t(option.title_key)}
              isExpanded={isExpanded}
              onToggle={() => toggleGroup(option.title_key)}
              action={isReportsGroup && canWriteReports ? <CreateReportPopover /> : undefined}
            />

            {isExpanded && (
              <div className="mt-1 ml-3 space-y-0.5 border-l border-border pl-3">
                {option.items
                  .filter(({ link }) => !hideHumanAgents || link !== HUMAN_AGENTS_LINK)
                  .map(({ text_key, link }) => (
                    <NavLink
                      key={text_key}
                      to={`/${slug}/analytics${link}`}
                      end
                      className={navLinkClass}
                    >
                      <InfoTooltip content={t(text_key)} side="right" size="auto">
                        <span className="min-w-0 truncate">{t(text_key)}</span>
                      </InfoTooltip>
                    </NavLink>
                  ))}
                {isReportsGroup && slug && canReadReports && <ReportNavItems slug={slug} />}
              </div>
            )}
          </div>
        );
      })}
    </SubNavShell>
  );
};

export default AnalyticsNavigation;
